import { PayloadAction } from "@reduxjs/toolkit";
import { takeLatest, all, call, put, select } from "redux-saga/effects";

import { Roles, Tenants } from "config";
import { HttpError, HttpStatusCodes } from "infrastructure/http";
import { ILocalizable, ILocale, commonMessages, isLocalizable } from "infrastructure/localization";
import { notification, INotification } from "infrastructure/notification";
import { IValidationIssue, ValidationIssueSeverity } from "infrastructure/validation";
import { IPickerSearchCriterion, applyPickerFilter } from "ui/shared/helpers/plex-components";
import { IIndustry, extractApiErrorMessageContent } from "ui/shared/models/common";

import { mapParentRequest } from "../shared/helpers/ParentFlag.helper";
import { createTextPreview } from "../shared/helpers/TextPreview.helpers";
import { IChangeToken } from "../shared/types/Token.types";
import { displayMessages } from "../translation-managers.localization";

import { plexTranslationMessagesActions } from "./PlexTranslationMessages.actions";
import { plexTranslationMessagesManagerApi as api } from "./PlexTranslationMessages.api";
import {
  IPlexTranslationMessagesGetRequest,
  IPlexTranslationMessageSaveRequest,
  IPlexTranslationApprove,
  IPlexTranslationsApproveRequest,
  IPlexTranslationsRequestRequest,
  IPlexTranslationsProposeRequest,
  IPlexTranslationRequest,
  IPlexTranslationsPropose,
  IPlexTranslationsRequestAccountsRequest,
  IPlexTranslationsObsoleteRequest,
  IPlexTranslationsObsolete,
  IPlexTranslationsReassignRequest,
  IPlexTranslationsRejectRequest,
  IPlexTranslationReview,
  IPlexTranslationsReviewRequest
} from "./PlexTranslationMessages.api.types";
import { getPlexTranslationModifyData } from "./PlexTranslationMessages.helpers";
import {
  selectors,
  IPlexTranslationMessagesManagerFilter,
  IPlexTranslationMessagesManagerFilterData,
  IPlexTranslationMessageItem,
  IPlexTranslationMessageRow,
  IChangeText,
  IPlexTranslationMessageModifyData,
  IPlexTranslationErrorModel,
  SelectorState,
  IAccount
} from "./PlexTranslationMessages.state";

function* getChangedRows() {
  const rows: IPlexTranslationMessageRow[] = yield select(selectors.getGridRows);
  return rows.filter(row => row.isTranslationChanged);
}

function* getNotValidRows() {
  const rows: IPlexTranslationMessageRow[] = yield select(selectors.getGridRows);
  return rows.filter(row => row.validationIssues.length);
}

function* resetValidationState() {
  const rows: IPlexTranslationMessageRow[] = yield call(getNotValidRows);

  const updatedRecords = rows.map(r => {
    return getPlexTranslationModifyData(r, [], r.data.translatedText);
  });

  yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
}

function* initializeExecute() {
  try {
    const [statuses, parents]: [ILocalizable[], ILocalizable[]] = yield all([call(api.getTranslationStatuses), call(api.getParentOptions)]);

    const filter: IPlexTranslationMessagesManagerFilter = {
      locales: [],
      industries: [],
      text: "",
      messageId: "",
      statuses: statuses,
      assignedToAccounts: [],
      parents: parents,
      isCheckedShowPreviewText: false,
      supportedLocales: []
    };

    yield put(plexTranslationMessagesActions.initializeCompleted(filter));
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* searchExecute() {
  try {
    const filter: IPlexTranslationMessagesManagerFilter = yield select(selectors.getFilter);
    const searchData = filter.lastData;
    if (searchData) {
      if (!searchData.selectedLocale) {
        yield put(plexTranslationMessagesActions.loadingFailed(notification.warning().useMessageId(displayMessages.error("localeRequired"))));
      } else {
        const request: IPlexTranslationMessagesGetRequest = {
          locale: searchData.selectedLocale.localeIdentifier,
          industry: searchData.selectedIndustry?.isiCv4Code,
          text: searchData.filterText,
          messageId: searchData.filterMessageId,
          status: searchData.selectedStatus?.messageId.value,
          assignedTo: searchData.selectedAssignedTo?.accountId,
          parent: searchData.selectedParent && mapParentRequest(searchData.selectedParent)
        };

        const plexTranslationMessages: IPlexTranslationMessageItem[] = yield call(api.getPlexTranslationMessages, request);

        const translationRows: IPlexTranslationMessageRow[] = plexTranslationMessages.map((item, index) => {
          const tokens = item.tokens.map((t, index) => {
            return {
              index: index,
              name: t.name,
              description: t.description,
              type: t.type,
              value: null,
              originalValue: null,
              isTokenValueChanged: false
            };
          });

          return {
            data: {
              locale: item.locale,
              industry: item.industry,
              messageId: item.messageId,
              sourceText: item.sourceText,
              translatedText: item.translatedText,
              tokens: tokens,
              textPreview: createTextPreview(item.translatedText ? item.translatedText : "", tokens, item.locale),
              translationStatus: item.translationStatus ? item.translationStatus : "",
              version: item.version,
              isParentMessage: item.isParentMessage,
              assignedTo: item.assignedTo
            },
            index: index,
            isTranslationChanged: false,
            isTranslationDefinitionChanged: false,
            validationIssues: []
          };
        });

        yield put(plexTranslationMessagesActions.searchCompleted(translationRows));
      }
    }
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* changePlexTranslationExecute({ payload }: PayloadAction<IChangeText>) {
  const rows: IPlexTranslationMessageRow[] = yield select(selectors.getGridRows);
  const row = rows[payload.index];

  const issues = [];
  const changedData = getPlexTranslationModifyData(row, issues, payload.text);

  yield put(plexTranslationMessagesActions.applyPlexTranslationChange(changedData));
}

function* changeTokenExecute({ payload }: PayloadAction<IChangeToken>) {
  yield put(plexTranslationMessagesActions.applyChangeTokenValue(payload));
}

function* getErrorRecords(error: HttpError) {
  const errorModels: IPlexTranslationErrorModel[] = error.data;
  const rows: IPlexTranslationMessageRow[] = yield select(selectors.getGridRows);

  const updatedRecords: IPlexTranslationMessageModifyData[] = [];

  for (const row of rows) {
    const issues: IValidationIssue[] = [];

    for (const model of errorModels) {
      if (row.data.messageId === model.messageId) {
        issues.push({
          message: extractApiErrorMessageContent(model.error),
          severity: ValidationIssueSeverity.Error,
          tag: "Translation"
        });
      }
    }

    const data = getPlexTranslationModifyData(row, issues, row.data.translatedText);
    updatedRecords.push(data);
  }

  return updatedRecords;
}

function* savePlexTranslationsExecute() {
  try {
    const filterData: IPlexTranslationMessagesManagerFilterData | undefined = yield select(selectors.getFilterLastData);

    if (filterData?.selectedLocale) {
      const rows: IPlexTranslationMessageRow[] = yield call(getChangedRows);
      const changedRows = rows.map(row => {
        const data = row.data;

        const rowData: IPlexTranslationMessageSaveRequest = {
          messageId: data.messageId,
          text: data.translatedText,
          locale: filterData.selectedLocale?.localeIdentifier,
          industry: filterData.selectedIndustry?.isiCv4Code,
          version: data.version
        };
        return rowData;
      });

      if (changedRows.length === 0) {
        yield call(resetValidationState);
        yield put(
          plexTranslationMessagesActions.savePlexTranslationsCompleted({ message: notification.warning().useMessageId(commonMessages.info("noRecordsModified")), isLoading: false })
        );
      } else {
        yield call(api.savePlexTranslations, changedRows);

        yield put(plexTranslationMessagesActions.search());
        yield put(
          plexTranslationMessagesActions.savePlexTranslationsCompleted({ message: notification.success().useMessageId(displayMessages.info("plexTranslationsSaveCompleted")) })
        );
      }
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsSaveFailed"))));
  }
}

function* applyPlexTranslationChangesExecute() {
  const rows: IPlexTranslationMessageRow[] = yield call(getChangedRows);
  const notifications: INotification[] = yield select(selectors.getNotifications);

  const warningMessageId = displayMessages.info("updatedItemsNotSaved");
  const hasWarningNotification = notifications.some(n => n.content.some(c => isLocalizable(c.message) && c.message.messageId.value === warningMessageId.value));

  if (rows.length > 0) {
    if (!hasWarningNotification) {
      yield put(plexTranslationMessagesActions.applyPlexTranslationChangesWarning(notification.warning().useMessageId(warningMessageId)));
    }
  } else {
    if (hasWarningNotification) {
      yield put(plexTranslationMessagesActions.applyPlexTranslationChangesWarning());
    }
  }
}

function* loadIndustriesExecute({ payload }: PayloadAction<IPickerSearchCriterion<IIndustry>>) {
  try {
    let industries: IIndustry[] = yield call(api.getIndustries, payload.query.query);
    industries = applyPickerFilter(industries, payload, row => row.description);

    yield put(plexTranslationMessagesActions.loadIndustriesCompleted(industries));
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadingFailed(notification.error(error).useMessageId(displayMessages.error("loadIndustriesFailed"))));
  }
}

function* loadLocalesExecute({ payload }: PayloadAction<IPickerSearchCriterion<ILocale>>) {
  try {
    const locales: ILocale[] = yield call(api.getLocales, payload?.query?.query);
    applyPickerFilter(locales, payload, row => row.name);
    applyPickerFilter(locales, payload, row => row.localeIdentifier);

    yield put(plexTranslationMessagesActions.loadLocalesCompleted(locales));
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("loadLocalesFailed"))));
  }
}

function* downloadXmlTemplateExecute() {
  try {
    yield call(api.downloadXmlTemplate);
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadingFailed(notification.error(error).useMessageId(commonMessages.error("searchFailed"))));
  }
}

function* exportTranslationsToXmlExecute() {
  try {
    const filter: IPlexTranslationMessagesManagerFilter = yield select(selectors.getFilter);
    const searchData = filter.lastData;
    if (searchData) {
      if (searchData.selectedLocale) {
        const request: IPlexTranslationMessagesGetRequest = {
          locale: searchData.selectedLocale.localeIdentifier,
          industry: searchData.selectedIndustry?.isiCv4Code,
          text: searchData.filterText,
          messageId: searchData.filterMessageId,
          status: searchData.selectedStatus?.messageId.value,
          parent: searchData.selectedParent && mapParentRequest(searchData.selectedParent)
        };

        yield call(api.exportPlexTranslationsToXml, request);
      } else {
        yield put(plexTranslationMessagesActions.loadingFailed(notification.warning().useMessageId(displayMessages.error("localeRequired"))));
      }
    }
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadingFailed(notification.error(error).useMessageId(displayMessages.error("exportFailed"))));
  }
}

function* approvePlexTranslationsExecute() {
  try {
    const [rows, filterData]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData
    ]);

    if (filterData?.selectedLocale) {
      const selectedRows: IPlexTranslationApprove[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version
        };
      });

      const request: IPlexTranslationsApproveRequest = {
        industry: filterData.selectedIndustry?.isiCv4Code,
        locale: filterData.selectedLocale?.localeIdentifier,
        translations: selectedRows
      };

      if (request.locale && request.industry) {
        yield call(api.approvePlexTranslationsWithIndustry, request);
      } else if (request.locale) {
        yield call(api.approvePlexTranslations, request);
      }

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.approvePlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsApproveCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsApproveFailed"))));
  }
}

function* requestPlexTranslationsExecute() {
  try {
    const [rows, filterData, assignee]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined, IAccount] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData,
      state.plexTranslationMessagesManager.actionBar.accountSelected
    ]);

    if (filterData?.selectedLocale) {
      const selectedRows: IPlexTranslationRequest[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version,
          assignee: assignee.accountId
        };
      });

      const request: IPlexTranslationsRequestRequest = {
        industry: filterData.selectedIndustry?.isiCv4Code,
        locale: filterData.selectedLocale?.localeIdentifier,
        translations: selectedRows
      };

      if (request.locale && request.industry) {
        yield call(api.requestPlexTranslationsWithIndustry, request);
      } else if (request.locale) {
        yield call(api.requestPlexTranslations, request);
      }

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.requestPlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsRequestCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsRequestFailed"))));
  }
}

function* reassignPlexTranslationsExecute() {
  try {
    const [rows, filterData, assignee]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined, IAccount] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData,
      state.plexTranslationMessagesManager.actionBar.accountSelected
    ]);

    if (filterData?.selectedLocale) {
      const selectedRows: IPlexTranslationRequest[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version,
          assignee: assignee.accountId
        };
      });

      const request: IPlexTranslationsReassignRequest = {
        industry: filterData.selectedIndustry?.isiCv4Code,
        locale: filterData.selectedLocale?.localeIdentifier,
        translations: selectedRows
      };

      if (request.locale && request.industry) {
        yield call(api.reassignPlexTranslationsWithIndustry, request);
      } else if (request.locale) {
        yield call(api.reassignPlexTranslations, request);
      }

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.reassignPlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsReassignCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsReassignFailed"))));
  }
}

function* reviewPlexTranslationsExecute() {
  try {
    const [rows, filterData, comment]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined, string] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData,
      state.plexTranslationMessagesManager.actionBar.review?.comment
    ]);

    if (filterData?.selectedLocale) {
      const selectedRows: IPlexTranslationReview[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version,
          comment: comment
        };
      });

      const request: IPlexTranslationsReviewRequest = {
        industry: filterData.selectedIndustry?.isiCv4Code,
        locale: filterData.selectedLocale?.localeIdentifier,
        translations: selectedRows
      };

      if (request.locale && request.industry) {
        yield call(api.reviewPlexTranslationsWithIndustry, request);
      } else if (request.locale) {
        yield call(api.reviewPlexTranslations, request);
      }

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.reviewPlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsReviewCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsReviewFailed"))));
  }
}

function* proposePlexTranslationsExecute() {
  try {
    const [rows, filterData]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData
    ]);

    if (filterData?.selectedLocale?.localeIdentifier) {
      const selectedRows: IPlexTranslationsPropose[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          industry: filterData.selectedIndustry?.isiCv4Code,
          locale: filterData.selectedLocale?.localeIdentifier ?? "",
          text: row.data.translatedText,
          version: row.data.version
        };
      });

      const request: IPlexTranslationsProposeRequest = {
        translations: selectedRows
      };

      yield call(api.proposePlexTranslations, request);

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.proposePlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsProposeCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsProposeFailed"))));
  }
}

function* rejectPlexTranslationsExecute() {
  try {
    const [rows, filterData, assignee]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined, IAccount] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData,
      state.plexTranslationMessagesManager.actionBar.accountSelected
    ]);

    if (filterData?.selectedLocale) {
      const selectedRows: IPlexTranslationRequest[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version,
          assignee: assignee.accountId
        };
      });

      const request: IPlexTranslationsRejectRequest = {
        industry: filterData.selectedIndustry?.isiCv4Code,
        locale: filterData.selectedLocale?.localeIdentifier,
        translations: selectedRows
      };

      if (request.locale && request.industry) {
        yield call(api.rejectPlexTranslationsWithIndustry, request);
      } else if (request.locale) {
        yield call(api.rejectPlexTranslations, request);
      }

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.rejectPlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsRejectCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsReassignFailed"))));
  }
}

function* obsoletePlexTranslationsExecute() {
  try {
    const [rows, filterData]: [IPlexTranslationMessageRow[], IPlexTranslationMessagesManagerFilterData | undefined] = yield select((state: SelectorState) => [
      state.plexTranslationMessagesManager.grid.selected,
      state.plexTranslationMessagesManager.filter.lastData
    ]);

    if (filterData?.selectedLocale) {
      const selectedRows: IPlexTranslationsObsolete[] = rows.map(row => {
        return {
          messageId: row.data.messageId,
          version: row.data.version
        };
      });

      const request: IPlexTranslationsObsoleteRequest = {
        industry: filterData.selectedIndustry?.isiCv4Code,
        locale: filterData.selectedLocale.localeIdentifier,
        translations: selectedRows
      };

      if (request.industry) {
        yield call(api.obsoleteIndustryTranslations, request);
      } else {
        yield call(api.obsoletePlexTranslations, request);
      }

      yield put(plexTranslationMessagesActions.search());
      yield put(plexTranslationMessagesActions.obsoletePlexTranslationsCompleted(notification.success().useMessageId(displayMessages.info("plexTranslationsObsoleteCompleted"))));
    }
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === HttpStatusCodes.BadRequest) {
      const updatedRecords: IPlexTranslationMessageModifyData[] = yield call(getErrorRecords, error);

      yield put(plexTranslationMessagesActions.applyPlexTranslationsChanges(updatedRecords));
    }

    yield put(plexTranslationMessagesActions.loadingFailed(notification.error().useMessageId(displayMessages.error("plexTranslationsObsoleteFailed"))));
  }
}

function* loadAccountsExecute({ payload }: PayloadAction<IPickerSearchCriterion<IAccount>>) {
  try {
    const request: IPlexTranslationsRequestAccountsRequest = {
      securityRoleId: Roles.Translator,
      tenantId: Tenants.Plex,
      accountName: payload?.query?.query
    };
    const accounts: IAccount[] = yield call(api.getPlexAccounts, request);

    yield put(plexTranslationMessagesActions.loadPickerAccountsCompleted(accounts));
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadPickerAccountsFailed(error));
  }
}

function* loadAssignedToAccountsExecute({ payload }: PayloadAction<IPickerSearchCriterion<IAccount>>) {
  try {
    const request: IPlexTranslationsRequestAccountsRequest = {
      securityRoleId: Roles.Translator,
      tenantId: Tenants.Plex,
      accountName: payload?.query?.query
    };
    const accounts: IAccount[] = yield call(api.getPlexAccounts, request);

    yield put(plexTranslationMessagesActions.loadAssignedToAccountsCompleted(accounts));
  } catch (error) {
    yield put(plexTranslationMessagesActions.loadAssignedToAccountsFailed(error));
  }
}

function* actionWatcher() {
  yield takeLatest(plexTranslationMessagesActions.initialize.toString(), initializeExecute);
  yield takeLatest(plexTranslationMessagesActions.search.toString(), searchExecute);
  yield takeLatest(plexTranslationMessagesActions.loadIndustries.toString(), loadIndustriesExecute);
  yield takeLatest(plexTranslationMessagesActions.loadLocales.toString(), loadLocalesExecute);
  yield takeLatest(plexTranslationMessagesActions.loadAssignedToAccounts.toString(), loadAssignedToAccountsExecute);
  yield takeLatest(plexTranslationMessagesActions.loadPickerAccounts.toString(), loadAccountsExecute);
  yield takeLatest(plexTranslationMessagesActions.savePlexTranslations.toString(), savePlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.applyPlexTranslationsChanges.toString(), applyPlexTranslationChangesExecute);
  yield takeLatest(plexTranslationMessagesActions.changeTokenValue.toString(), changeTokenExecute);
  yield takeLatest(plexTranslationMessagesActions.changePlexTranslation.toString(), changePlexTranslationExecute);
  yield takeLatest(plexTranslationMessagesActions.downloadPlexTranslationUploadTemplate.toString(), downloadXmlTemplateExecute);
  yield takeLatest(plexTranslationMessagesActions.exportPlexTranslationsToXml.toString(), exportTranslationsToXmlExecute);
  yield takeLatest(plexTranslationMessagesActions.approvePlexTranslations.toString(), approvePlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.requestPlexTranslations.toString(), requestPlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.reassignPlexTranslations.toString(), reassignPlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.reviewPlexTranslations.toString(), reviewPlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.proposePlexTranslations.toString(), proposePlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.rejectPlexTranslations.toString(), rejectPlexTranslationsExecute);
  yield takeLatest(plexTranslationMessagesActions.obsoletePlexTranslations.toString(), obsoletePlexTranslationsExecute);
}

export function* plexTranslationMessagesManagerSaga() {
  yield all([actionWatcher()]);
}
